/**
 * Pure logic for adaptive screen-share quality («Авто» mode).
 *
 * The session samples the video sender's stats every few seconds and feeds a
 * tick into {@link nextAutoQuality}. When the encoder reports sustained
 * bandwidth/CPU limitation we step DOWN the quality ladder (built once from
 * the user's chosen tier); after a long stable stretch we step back UP.
 * Hysteresis (2 bad ticks to drop, 10 good ticks to rise) prevents flapping
 * on a bursty uplink.
 */
import type { ScreenFps, ScreenResolution } from '@/lib/voicePreferences';

export interface ScreenQualityStep {
  resolution: ScreenResolution;
  fps: ScreenFps;
}

/** Resolution tiers in descending order (source ≈ native, treated as the
 *  1440p-class tier for laddering). */
const RES_ORDER: readonly Exclude<ScreenResolution, 'source'>[] = ['4k', '1440p', '1080p', '720p'];

function resTierIndex(resolution: ScreenResolution): number {
  if (resolution === 'source') return RES_ORDER.indexOf('1440p');
  return RES_ORDER.indexOf(resolution);
}

/**
 * Quality ladder from the user's chosen tier down to the floor (720p15),
 * best → worst. Step order: shave fps from 60 first (cheap bandwidth win,
 * screen content tolerates it), then walk resolution tiers down at 30fps,
 * then drop to 15fps at the floor. The user's own choice is always level 0 —
 * auto mode never upgrades past what they picked.
 */
export function buildQualityLadder(start: ScreenQualityStep): ScreenQualityStep[] {
  const ladder: ScreenQualityStep[] = [start];
  let fps: ScreenFps = start.fps;
  if (fps === 60) {
    fps = 30;
    ladder.push({ resolution: start.resolution, fps });
  }
  for (let i = resTierIndex(start.resolution) + 1; i < RES_ORDER.length; i++) {
    const resolution = RES_ORDER[i];
    if (!resolution) continue;
    ladder.push({ resolution, fps: fps === 15 ? 15 : 30 });
  }
  const last = ladder[ladder.length - 1];
  if (last && last.resolution === '720p' && last.fps > 15) {
    ladder.push({ resolution: '720p', fps: 15 });
  }
  return ladder;
}

export interface AutoQualityTick {
  /** Encoder reported qualityLimitationReason 'bandwidth' this sample. */
  bandwidthLimited: boolean;
  /** Encoder reported qualityLimitationReason 'cpu' this sample. */
  cpuLimited: boolean;
}

export interface AutoQualityState {
  /** Current ladder level (0 = the user's chosen tier). */
  level: number;
  badTicks: number;
  goodTicks: number;
}

export const AUTO_QUALITY_INITIAL: AutoQualityState = { level: 0, badTicks: 0, goodTicks: 0 };

/** Consecutive limited samples before stepping down (~2 ticks ≈ 6s). */
const DOWN_AFTER_BAD_TICKS = 2;
/** Consecutive clean samples before stepping back up (~10 ticks ≈ 30s). */
const UP_AFTER_GOOD_TICKS = 10;

/**
 * Advance the auto-quality state machine by one stats sample. Returns the new
 * state and whether the ladder level changed (the caller re-applies
 * constraints + bitrate only on change).
 */
export function nextAutoQuality(
  state: AutoQualityState,
  tick: AutoQualityTick,
  maxLevel: number,
): { state: AutoQualityState; changed: boolean } {
  const limited = tick.bandwidthLimited || tick.cpuLimited;
  if (limited) {
    const badTicks = state.badTicks + 1;
    if (badTicks >= DOWN_AFTER_BAD_TICKS && state.level < maxLevel) {
      return { state: { level: state.level + 1, badTicks: 0, goodTicks: 0 }, changed: true };
    }
    return { state: { ...state, badTicks, goodTicks: 0 }, changed: false };
  }
  const goodTicks = state.goodTicks + 1;
  if (goodTicks >= UP_AFTER_GOOD_TICKS && state.level > 0) {
    return { state: { level: state.level - 1, badTicks: 0, goodTicks: 0 }, changed: true };
  }
  return { state: { ...state, goodTicks, badTicks: 0 }, changed: false };
}
